import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useNavigationStack } from '../context/NavigationStackContext';
import { useLanguage } from '../context/LanguageContext';
import { useManageMatterRequestContext } from '../context/ManageMatterRequest';
import { useUser } from '../context/UserContext';
import { ICustomerType } from '../constants/customer';
import { useEffect } from 'react';
import { IMatterRequest } from '../interface/matterRequest';
import { ROUTES } from '../constants/constants';
import { useFetchUserParameters } from './useFetchUserParameters';
import { useGetMatterTypeById } from './useGetMatterTypeById';
import { ParameterEntityType } from '../interface/common';
import { useDiscardDialog } from './useDiscardDialog';
import { isMatterRequestFormChanged } from '../components/matterRequests/isMatterRequestFormChanged';
import { IMatterType } from '../interface/matter';
import { IUser } from '../interface/user';

interface UseManageMatterRequestLogicOptions {
  isFromMailOrAppointment?: boolean;
  mailSubject?: string;
}

const resolveMatterTypeLanguageCurrencyDefaults = (
  matterRequest: IMatterRequest | null,
  isDataReady: boolean,
  defaultParams: { language?: string; currency?: string } | undefined,
  matterTypeDetails: IMatterType | undefined,
): Partial<IMatterRequest> => {
  if (!isDataReady || !defaultParams) return {};

  const updates: Partial<IMatterRequest> = {};

  if (
    !matterRequest?.matterRequestTypeId &&
    !matterRequest?.isMatterTypeSetByUser &&
    matterTypeDetails
  ) {
    updates.matterRequestType = matterTypeDetails;
    updates.matterRequestTypeId = matterTypeDetails.id;
  }
  if (!matterRequest?.language && !matterRequest?.isLanguageSetByUser && defaultParams.language) {
    updates.language = defaultParams.language;
  }
  if (!matterRequest?.currency && !matterRequest?.isCurrencySetByUser && defaultParams.currency) {
    updates.currency = defaultParams.currency;
  }
  return updates;
};

const resolveOwnerResponsibleDefaults = (
  matterRequest: IMatterRequest | null,
  user: IUser | null,
): Partial<IMatterRequest> => {
  if (!user?.id) return {};

  const updates: Partial<IMatterRequest> = {};
  if (!matterRequest?.ownerUserId && !matterRequest?.isOwnerSetByUser) {
    updates.ownerUserId = user.id;
    updates.ownerUser = user;
  }
  if (!matterRequest?.responsibleUserId && !matterRequest?.isResponsibleSetByUser) {
    updates.responsibleUserId = user.id;
    updates.responsibleUser = user;
  }
  return updates;
};

export const useManageMatterRequestLogic = (options?: UseManageMatterRequestLogicOptions) => {
  const { isFromMailOrAppointment, mailSubject } = options ?? {};
  const { goBack, push } = useNavigationStack();
  const { language: appLanguage } = useLanguage();
  const { t } = useTranslation();
  const { id } = useParams();
  const isEditing = Boolean(id);
  const { matterRequest, initialManageMatterRequest, updateManageMatterRequest } =
    useManageMatterRequestContext();
  const { user } = useUser();
  const { showDiscardDialog, promptDiscardIfChanged, handleDiscardConfirm, handleDiscardCancel } =
    useDiscardDialog();
  const { data: parametersDetails, isLoading: isParameterLoading } = useFetchUserParameters({
    parametersToGet: ParameterEntityType.DEFAULT,
  });
  const { data: matterTypeDetails, isLoading: isMatterTypeLoading } = useGetMatterTypeById({
    id: parametersDetails?.parameters?.defaultParametersResponse?.matterTypeId,
  });
  // A single effect applying every mount-time default/derived update atomically.
  // Splitting these across sibling effects lets them fire within the same render
  // commit (e.g. once a cached query resolves instantly); since each would spread
  // the same pre-update `matterRequest` snapshot, whichever ran last silently
  // dropped the others' fields. One effect, one merged update, avoids that race.
  useEffect(() => {
    const updates: Partial<IMatterRequest> = {};

    if (!isEditing) {
      const isMatterDataReady = !isParameterLoading && !isMatterTypeLoading && !!parametersDetails;
      const defaultParams = parametersDetails?.parameters?.defaultParametersResponse;

      Object.assign(
        updates,
        resolveMatterTypeLanguageCurrencyDefaults(
          matterRequest,
          isMatterDataReady,
          defaultParams,
          matterTypeDetails,
        ),
        resolveOwnerResponsibleDefaults(matterRequest, user),
      );
    }

    if (!matterRequest?.currentStepOption) {
      updates.currentStepOption = 'existing';
      updates.newCustomer = {
        ...matterRequest?.newCustomer,
        type: ICustomerType.COMPANY,
      } as unknown as IMatterRequest['newCustomer'];
    }

    if (
      !isEditing &&
      isFromMailOrAppointment &&
      mailSubject &&
      matterRequest?.lastAppliedMailSubject !== mailSubject
    ) {
      updates.isFromMailOrAppointment = true;
      updates.subject = mailSubject;
      updates.isSubjectSetByUser = false;
      updates.lastAppliedMailSubject = mailSubject;
    }

    if (Object.keys(updates).length > 0) {
      updateManageMatterRequest({
        ...matterRequest,
        ...updates,
      } as unknown as IMatterRequest);
    }
  }, [
    isEditing,
    isParameterLoading,
    isMatterTypeLoading,
    parametersDetails,
    matterTypeDetails,
    user,
    matterRequest?.matterRequestTypeId,
    matterRequest?.isMatterTypeSetByUser,
    matterRequest?.language,
    matterRequest?.isLanguageSetByUser,
    matterRequest?.currency,
    matterRequest?.isCurrencySetByUser,
    matterRequest?.ownerUserId,
    matterRequest?.isOwnerSetByUser,
    matterRequest?.responsibleUserId,
    matterRequest?.isResponsibleSetByUser,
    matterRequest?.currentStepOption,
    isFromMailOrAppointment,
    mailSubject,
    matterRequest?.lastAppliedMailSubject,
  ]);

  const handleBack = () => {
    if (matterRequest?.currentStep === 'details' && matterRequest?.currentStepOption === 'new') {
      updateManageMatterRequest({
        currentStep: 'newCustomer',
      } as IMatterRequest);
      return;
    }
    promptDiscardIfChanged(
      isMatterRequestFormChanged(matterRequest, initialManageMatterRequest, isEditing),
      () => {
        updateManageMatterRequest(null, true);
        goBack();
      },
    );
  };

  const handleSelectCustomer = () => {
    if (matterRequest?.currentStepOption === 'new') {
      updateManageMatterRequest({
        ...matterRequest,
        currentStep: 'newCustomer',
      } as unknown as IMatterRequest);
      return;
    }
    push(
      `${ROUTES.CUSTOMER_LISTING}?isFromMatterRequest=true&selectedCustomerId=${matterRequest?.principalCompanyId ?? matterRequest?.principalPrivatePersonId ?? ''}`,
      { returnFocusTo: 'customer' },
    );
  };

  const handleSelectMatterRequestType = () => {
    push(ROUTES.MATTER_REQUEST_TYPE_LISTING, { returnFocusTo: 'matterRequestType' });
  };

  const handleSelectLanguage = () => {
    push(ROUTES.LANGUAGE_LISTING, { returnFocusTo: 'language' });
  };

  const handleSelectCurrency = () => {
    push(ROUTES.CURRENCY_LISTING, { returnFocusTo: 'currency' });
  };

  const handleSelectHomeCountry = () => {
    push(ROUTES.COUNTRY_LISTING, { returnFocusTo: 'homeCountry' });
  };

  const handleSelectOwner = () => {
    push(ROUTES.MATTER_REQUEST_USER_LISTING, { field: 'owner' });
  };

  const handleSelectResponsible = () => {
    push(ROUTES.MATTER_REQUEST_USER_LISTING, { field: 'responsible' });
  };

  const handleSelectBusinessArea = () => {
    push(ROUTES.BUSINESS_AREA_LISTING);
  };

  const handleSelectCustomerType = () => {
    push(ROUTES.MATTER_REQUEST_TYPE_LISTING, {
      isCustomerTypeSelection: true,
      returnFocusTo: 'customerType',
    });
  };

  const handleContinueToDetails = () => {
    updateManageMatterRequest({
      ...matterRequest,
      currentStep: 'details',
    } as unknown as IMatterRequest);
  };

  const getCustomerInputValue = () => {
    if (matterRequest?.currentStepOption === 'new') {
      const newCustomerName = matterRequest?.newCustomer?.name ?? '';
      const newCustomerLastName = matterRequest?.newCustomer?.lastName ?? '';
      return matterRequest?.newCustomer?.type === ICustomerType.PRIVATE_PERSON
        ? `${newCustomerName} ${newCustomerLastName}`.trim()
        : newCustomerName;
    }
    return (
      matterRequest?.principalCompany?.name || matterRequest?.principalPrivatePerson?.fullName || ''
    );
  };

  const handleCustomerOptionChange = (newOption: 'existing' | 'new') => {
    updateManageMatterRequest({
      ...matterRequest,
      currentStepOption: newOption,
      currentStep: newOption === 'new' ? 'newCustomer' : 'details',
    } as unknown as IMatterRequest);
  };

  return {
    t,
    appLanguage,
    isEditing,
    matterRequest,
    updateManageMatterRequest,
    handleBack,
    handleSelectCustomer,
    handleSelectMatterRequestType,
    handleSelectLanguage,
    handleSelectCurrency,
    handleSelectHomeCountry,
    handleSelectCustomerType,
    handleSelectOwner,
    handleSelectResponsible,
    handleSelectBusinessArea,
    handleContinueToDetails,
    getCustomerInputValue,
    handleCustomerOptionChange,
    showDiscardDialog,
    handleDiscardConfirm,
    handleDiscardCancel,
  };
};
